import { Alert, Button, Card, Form, InputNumber, Select, Switch, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Settings = {
  company_id: string;
  retrospective_days: number;
  holiday_policy: string;
  alert_on_absent: boolean;
  repeated_absence_after: number | null;
  record_version: number;
  configured: boolean;
};

type ApiError = { response?: { data?: { message?: string } } };

export default function AttendanceSettingsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const query = useQuery({
    queryKey: ['education-attendance-settings'],
    queryFn: () => api.get<Settings>('/education/attendance/settings'),
  });
  const settings = query.data?.data;

  useEffect(() => {
    if (!settings) return;
    form.setFieldsValue({
      holiday_policy: settings.holiday_policy || 'block',
      alert_on_absent: Boolean(settings.alert_on_absent),
      retrospective_days: settings.retrospective_days ?? 0,
      repeated_absence_after: settings.repeated_absence_after,
      record_version: settings.record_version,
    });
  }, [form, settings]);

  const save = useMutation({
    mutationFn: (values: {
      holiday_policy: string;
      alert_on_absent: boolean;
      retrospective_days: number;
      repeated_absence_after?: number | null;
      record_version: number;
    }) =>
      api.put<Settings>('/education/attendance/settings', {
        ...values,
        alert_on_absent: values.alert_on_absent ? 1 : 0,
        repeated_absence_after: values.repeated_absence_after ?? null,
      }),
    onSuccess: () => {
      message.success(t('attendanceSettingsSaved', { defaultValue: 'Attendance settings saved' }));
      queryClient.invalidateQueries({ queryKey: ['education-attendance-settings'] });
      queryClient.invalidateQueries({ queryKey: ['education-attendance-overview'] });
      queryClient.invalidateQueries({ queryKey: ['education-attendance-exceptions'] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('attendanceSettings')}
      description={t('attendanceSettingsIntro')}
      extra={<Button onClick={() => navigate('/education/attendance')}>{t('backToAttendance')}</Button>}
    >
      <Card size="small" loading={query.isLoading} style={{ maxWidth: 560 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('attendanceSettingsHint', {
            defaultValue:
              'Absence alerts use in-app notifications. Repeated-absence follow-up stays empty until a threshold is set. This is not HR attendance and not a CBSE percentage rule.',
          })}
        />
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Form.Item name="record_version" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="holiday_policy"
            label={t('holidayPolicy', { defaultValue: 'Holiday policy' })}
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'block', label: t('holidayPolicyBlock', { defaultValue: 'Block non-instructional days' }) },
                {
                  value: 'allow_explicit',
                  label: t('holidayPolicyAllow', { defaultValue: 'Allow with explicit reason' }),
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="retrospective_days"
            label={t('retrospectiveDays', { defaultValue: 'Retrospective marking window (days)' })}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} max={365} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="alert_on_absent"
            label={t('alertOnAbsent', { defaultValue: 'Alert guardians on absence' })}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="repeated_absence_after"
            label={t('repeatedAbsenceAfter', { defaultValue: 'Repeated-absence threshold' })}
            extra={t('repeatedAbsenceAfterHint', {
              defaultValue: 'Leave empty to disable the repeated-absence queue and notify.',
            })}
          >
            <InputNumber min={1} max={365} style={{ width: '100%' }} placeholder={t('thresholdNotConfigured')} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
